import Loading from '../../components/Loading'
import React, { useEffect } from 'react'
import { connect } from 'react-redux'
import {
  setTitle,
  getAll,
  cancelOrder,
  getOrderLabelUrl,
} from '../../actions'
import { Link } from 'react-router-dom'
import Button from '../../components/Button'
import Table from '../../components/Table'
import moment from 'moment'
import Swal from 'sweetalert2'
import { FiEye, FiXCircle } from 'react-icons/fi'
import { AiOutlineFilePdf } from 'react-icons/ai'
import formatNumbers from '../../utils/formatNumbers'

// El PDF no viene en la lista: se pide a GET /orders/:orderId/labels/:sequence.
const getFedexGuideDocuments = order => {
  const labels = Array.isArray(order.fedexGuideDocument)
    ? order.fedexGuideDocument
    : []

  return labels
    // Su guía viene dentro del PDF de otro paquete (Skydropx los junta).
    .filter(label => label && !label.labelInPackage)
    .map((label, index) => ({
      sequence: label.sequence || index + 1,
      tracking:
        label.trackingNumber ||
        `${order.fedexGuide || 'GUIA'}-${index + 1}`,
    }))
}

const openLabel = async (order, doc) => {
  // Se abre la pestaña antes del await para que el navegador no la bloquee.
  const tab = window.open('', '_blank')
  try {
    const url = await getOrderLabelUrl(
      order.orderId || order.orderNumber,
      doc.sequence
    )
    if (tab) tab.location.href = url
    else window.location.href = url
  } catch (error) {
    if (tab) tab.close()
    Swal.fire('Error', 'No se pudo abrir la guía.', 'error')
  }
}

const getCurrentStatus = order => {
  if (typeof order.status === 'string' && order.status.trim() !== '') {
    return order.status
  }

  const history =
    order.statusHistory ||
    order.status_history ||
    order.statuses ||
    order.orderStatuses ||
    []

  if (!Array.isArray(history) || history.length === 0) {
    return 'Sin estatus'
  }

  const sortedHistory = [...history].sort((a, b) => {
    const aDate =
      new Date(
        a.updated_at || a.created_at || a.created || a.date || 0
      ).getTime() || 0
    const bDate =
      new Date(
        b.updated_at || b.created_at || b.created || b.date || 0
      ).getTime() || 0

    return aDate - bDate
  })

  const latestStatus = sortedHistory[sortedHistory.length - 1]

  return (
    latestStatus?.status ||
    latestStatus?.name ||
    latestStatus?.label ||
    'Sin estatus'
  )
}

const getOrderId = order => order.id ?? order.orderId
const getOrderNumber = order =>
  order.orderNumber || order.order_number || order.sapOrderId || order.qt || '-'

const truncateOneLine = (text, max = 140) => {
  if (text == null || text === '') return '-'
  const s = String(text).replace(/\s+/g, ' ').trim()
  if (s.length <= max) return s
  return `${s.slice(0, max)}…`
}

const formatLastOrderError = order => {
  const step = order.lastErrorStep ?? order.last_error_step
  const msg = order.lastErrorMessage ?? order.last_error_message
  if (!step && !msg) return '-'
  const line = [step, msg].filter(Boolean).join(' — ')
  return truncateOneLine(line, 160)
}

const Orders = props => {
  const { setTitle, orders } = props

  useEffect(() => {
    setTitle({ title: 'Pedidos' })
    props.getAll('orders', 'GET_ORDERS')
    // eslint-disable-next-line
  }, [])

  if (!orders) return <Loading />

  const handleCancelOrder = order => {
    const orderId = getOrderId(order)

    Swal.fire({
      title: '¿Cancelar este pedido?',
      text: `Se marcará ${getOrderNumber(order)} como Cancelado.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Sí, cancelar',
      cancelButtonText: 'No',
    }).then(result => {
      if (!result.isConfirmed) return

      props
        .cancelOrder(orderId)
        .then(() => {
          props.getAll('orders', 'GET_ORDERS')
          Swal.fire('Cancelado', 'El pedido fue cancelado.', 'success')
        })
        .catch(error => {
          const message =
            error?.response?.data?.message ||
            'No se pudo cancelar el pedido.'
          Swal.fire('Error', message, 'error')
        })
    })
  }

  const columns = [
    {
      accessorKey: 'folio',
      header: 'Folio',
      Cell: ({ row }) => getOrderNumber(row.original),
    },
    {
      accessorKey: 'fedexGuide',
      header: 'Guia FedEx',
      Cell: ({ row }) => row.original.fedexGuide || '-',
    },
    {
      accessorKey: 'fedexGuideDocument',
      header: 'Documento guia',
      Cell: ({ row }) => {
        const documents = getFedexGuideDocuments(row.original)

        if (documents.length === 0) return '-'

        return (
          <div>
            {documents.map(doc => (
              <Button
                key={doc.sequence}
                title={`Descargar guía PDF ${doc.tracking}`}
                onClick={() => openLabel(row.original, doc)}
              >
                <AiOutlineFilePdf />
              </Button>
            ))}
          </div>
        )
      },
    },
    {
      accessorKey: 'createdAt',
      header: 'Fecha',
      Cell: ({ row }) => {
        const createdAt =
          row.original.createdAt ||
          row.original.created_at ||
          row.original.created ||
          row.original.date

        return createdAt
          ? moment(createdAt).format('DD/MM/YYYY')
          : '-'
      },
    },
    {
      accessorKey: 'total',
      header: 'Total',
      Cell: ({ row }) => {
        const v = Number(row.original.total ?? 0)
        if (!Number.isFinite(v)) return '-'
        return `$${formatNumbers(v, 2)}`
      },
    },
    {
      accessorKey: 'status',
      header: 'Estatus',
      Cell: ({ row }) => {
        const raw = getCurrentStatus(row.original)
        const statusMap = {
          created:            { label: 'Creado',              color: '#718096' },
          Pendiente:          { label: 'Pendiente',           color: '#ed8936' },
          PendienteReintento: { label: 'Pendiente reintento', color: '#dd6b20' },
          PendienteSurtido:   { label: 'Pendiente surtido',   color: '#d69e2e' },
          Enviado:            { label: 'Enviado',             color: '#3182ce' },
          Facturado:          { label: 'Facturado',           color: '#38a169' },
          Entregado:          { label: 'Entregado',           color: '#2f855a' },
          RequiereRevision:   { label: 'Requiere revisión',   color: '#c53030' },
          Cancelado:          { label: 'Cancelado',           color: '#9b2c2c' },
        }
        const { label, color } = statusMap[raw] || { label: raw || 'Sin estatus', color: '#718096' }
        return (
          <span style={{
            background: color,
            color: '#fff',
            padding: '2px 10px',
            borderRadius: '12px',
            fontSize: '0.8rem',
            fontWeight: 600,
            whiteSpace: 'nowrap',
          }}>
            {label}
          </span>
        )
      },
    },
    {
      accessorKey: 'lastOrderError',
      header: 'Último fallo (SAP/FedEx)',
      Cell: ({ row }) => (
        <span style={{ fontSize: '0.8rem', color: '#4a5568', whiteSpace: 'pre-wrap' }}>
          {formatLastOrderError(row.original)}
        </span>
      ),
    },
    {
      id: 'actions',
      accessorKey: 'actions',
      header: 'Detalles',
      Cell: ({ row }) => {
        const status = getCurrentStatus(row.original)
        const canCancel = status !== 'Entregado' && status !== 'Cancelado'

        return (
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <Link to={`/orders/detail/${getOrderId(row.original)}`}>
              <Button title="Ver detalle del pedido">
                <FiEye />
              </Button>
            </Link>
            {canCancel && (
              <Button
                title="Cancelar pedido"
                onClick={() => handleCancelOrder(row.original)}
              >
                <FiXCircle />
              </Button>
            )}
          </div>
        )
      },
    },
  ]

  return (
    <Table
      data={orders}
      columns={columns}
      name="Pedidos"
    />
  )
}

const mapStateToProps = state => ({
  orders: state.orders.orders,
})

const mapDispatchToProps = {
  setTitle,
  getAll,
  cancelOrder,
}

export default connect(mapStateToProps, mapDispatchToProps)(Orders)
